import React, { useEffect, useRef, useState } from "react";
import { apiFetch } from "./api";
import {
  DEFAULT_TEMPLATES,
  TOKENS,
  SAMPLE_CTX,
  buildEmailText,
} from "./emailTemplate";

export default function EmailTemplates({ onBack }) {
  const [templates, setTemplates] = useState([]);
  const [activeKey, setActiveKey] = useState("tavaklient");
  const [intro, setIntro] = useState("");
  const [closing, setClosing] = useState("");
  const [dirty, setDirty] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const introRef = useRef(null);
  const closingRef = useRef(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  function loadTemplates() {
    setLoading(true);
    setError("");
    apiFetch("/api/email-templates")
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const list = Array.isArray(data) && data.length ? data : DEFAULT_TEMPLATES;
        setTemplates(list);
        selectTemplate(list, activeKey);
      })
      .catch((err) => {
        console.error("Failed to load templates", err);
        setError("Mallide laadimine ebaõnnestus.");
        setTemplates(DEFAULT_TEMPLATES);
        selectTemplate(DEFAULT_TEMPLATES, activeKey);
      })
      .finally(() => setLoading(false));
  }

  function selectTemplate(list, key) {
    const t = list.find((x) => x.key === key) || list[0];
    if (!t) return;
    setActiveKey(t.key);
    setIntro(t.intro || "");
    setClosing(t.closing || "");
    setDirty(false);
  }

  function switchTemplate(key) {
    if (key === activeKey) return;
    if (dirty && !window.confirm("Salvestamata muudatused lähevad kaotsi. Jätkata?")) return;
    setSuccessMessage("");
    selectTemplate(templates, key);
  }

  // Insert a token at the cursor position of the given textarea.
  function insertToken(ref, value, setValue, token) {
    const el = ref.current;
    if (!el) {
      setValue(value + token);
      setDirty(true);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    setValue(next);
    setDirty(true);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  }

  async function save() {
    setSaving(true);
    setError("");
    setSuccessMessage("");
    try {
      const res = await apiFetch(`/api/email-templates/${activeKey}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intro, closing }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Salvestamine ebaõnnestus (HTTP ${res.status})`);

      setTemplates((prev) =>
        prev.map((t) => (t.key === activeKey ? { ...t, intro, closing } : t))
      );
      setDirty(false);
      setSuccessMessage("Mall salvestatud.");
    } catch (err) {
      console.error("Failed to save template", err);
      setError(err.message || "Salvestamine ebaõnnestus.");
    } finally {
      setSaving(false);
    }
  }

  const previewText = buildEmailText({ intro, closing }, SAMPLE_CTX);

  const tokenRow = (ref, value, setValue) => (
    <div style={styles.tokenRow}>
      {TOKENS.map((t) => (
        <button
          key={t.token}
          type="button"
          style={styles.tokenBtn}
          onClick={() => insertToken(ref, value, setValue, t.token)}
          title={`Lisa ${t.token}`}
        >
          + {t.label}
        </button>
      ))}
    </div>
  );

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <div>
          <h1 style={styles.title}>Mallid</h1>
          <div style={styles.subtitle}>
            Muuda e-kirja teksti. Kokkuvõte lisatakse automaatselt sissejuhatuse ja lõpetuse
            vahele.
          </div>
        </div>

        <div style={styles.topActions}>
          <button
            onClick={save}
            style={saving || !dirty ? styles.disabledBtn : styles.primaryBtn}
            type="button"
            disabled={saving || !dirty}
          >
            {saving ? "Salvestan…" : "Salvesta"}
          </button>

          <button onClick={onBack} style={styles.secondaryBtn} type="button">
            ← Tagasi
          </button>
        </div>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={styles.infoText}>Laen malle…</div>
        ) : (
          <>
            {/* Template selector */}
            <div style={styles.tabs}>
              {templates.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  style={t.key === activeKey ? styles.tabActive : styles.tab}
                  onClick={() => switchTemplate(t.key)}
                >
                  {t.name}
                </button>
              ))}
            </div>

            {error && <div style={styles.errorText}>{error}</div>}
            {successMessage && <div style={styles.successText}>{successMessage}</div>}

            <div style={styles.columns}>
              {/* Editor */}
              <div style={styles.col}>
                <label style={styles.label}>Sissejuhatus</label>
                <textarea
                  ref={introRef}
                  rows={6}
                  value={intro}
                  onChange={(e) => {
                    setIntro(e.target.value);
                    setDirty(true);
                  }}
                  style={styles.textarea}
                />
                {tokenRow(introRef, intro, setIntro)}

                <div style={styles.autoBlock}>— Kokkuvõte lisatakse siia automaatselt —</div>

                <label style={styles.label}>Lõpetus</label>
                <textarea
                  ref={closingRef}
                  rows={6}
                  value={closing}
                  onChange={(e) => {
                    setClosing(e.target.value);
                    setDirty(true);
                  }}
                  style={styles.textarea}
                />
                {tokenRow(closingRef, closing, setClosing)}
              </div>

              {/* Preview */}
              <div style={styles.col}>
                <label style={styles.label}>Eelvaade (näidisandmetega)</label>
                <div style={styles.preview}>{previewText}</div>
                <div style={styles.hint}>
                  Kasutatavad väljad: {TOKENS.map((t) => t.token).join("  ")}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#F3F6FD",
    fontFamily: "'DM Sans', sans-serif",
    padding: "28px",
  },

  topBar: {
    maxWidth: "1200px",
    margin: "0 auto 20px auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
  },

  topActions: { display: "flex", gap: "10px", alignItems: "center" },

  title: { margin: 0, color: "#0D1F4D", fontSize: "28px" },

  subtitle: { marginTop: "6px", color: "#555", fontSize: "14px" },

  card: {
    maxWidth: "1200px",
    margin: "0 auto",
    background: "white",
    borderRadius: "16px",
    padding: "18px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  },

  tabs: { display: "flex", gap: "8px", marginBottom: "16px" },

  tab: {
    background: "#F3F6FD",
    border: "1px solid #E6ECF8",
    borderRadius: "999px",
    padding: "8px 16px",
    cursor: "pointer",
    fontWeight: 700,
    color: "#0D1F4D",
    fontSize: "13px",
  },

  tabActive: {
    background: "#0D1F4D",
    border: "1px solid #0D1F4D",
    borderRadius: "999px",
    padding: "8px 16px",
    cursor: "pointer",
    fontWeight: 700,
    color: "white",
    fontSize: "13px",
  },

  columns: { display: "flex", gap: "24px", alignItems: "flex-start" },

  col: { flex: 1, minWidth: 0 },

  label: {
    display: "block",
    fontWeight: 600,
    color: "#0D1F4D",
    marginBottom: "6px",
    fontSize: "14px",
  },

  textarea: {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid #8CA5E6",
    fontSize: "14px",
    fontFamily: "'DM Sans', sans-serif",
    whiteSpace: "pre-wrap",
    resize: "vertical",
  },

  tokenRow: { display: "flex", gap: "6px", flexWrap: "wrap", margin: "8px 0 16px" },

  tokenBtn: {
    background: "#F3F6FD",
    border: "1px solid #E6ECF8",
    borderRadius: "999px",
    padding: "4px 10px",
    cursor: "pointer",
    fontWeight: 700,
    color: "#0D1F4D",
    fontSize: "12px",
  },

  autoBlock: {
    background: "#F8FAFF",
    border: "1px dashed #C7D3EE",
    borderRadius: "8px",
    padding: "12px",
    textAlign: "center",
    color: "#6C7A9A",
    fontSize: "13px",
    fontStyle: "italic",
    marginBottom: "16px",
  },

  preview: {
    background: "#F3F6FD",
    borderRadius: "10px",
    border: "1px solid #E6ECF8",
    padding: "14px",
    fontSize: "14px",
    color: "#0D1F4D",
    whiteSpace: "pre-wrap",
    minHeight: "300px",
  },

  hint: { marginTop: 10, fontSize: "12px", color: "#666" },

  infoText: { color: "#555", padding: "12px 4px" },

  errorText: { color: "#C0392B", padding: "0 0 12px 0", fontWeight: 600 },

  successText: { color: "#1E8449", padding: "0 0 12px 0", fontWeight: 600 },

  primaryBtn: {
    backgroundColor: "#0D1F4D",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "10px 16px",
    fontWeight: 600,
    cursor: "pointer",
  },

  secondaryBtn: {
    backgroundColor: "#8CA5E6",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "10px 14px",
    fontWeight: 600,
    cursor: "pointer",
  },

  disabledBtn: {
    backgroundColor: "#D8E1F7",
    color: "#6C7A9A",
    border: "none",
    borderRadius: "8px",
    padding: "10px 16px",
    fontWeight: 600,
    cursor: "not-allowed",
  },
};
